/**
 * Settings del portal, persistidos en localStorage.
 *
 * Son config del CLIENTE (a qué API apunta este navegador y con qué token),
 * no del dominio: nada de esto viaja en el log de eventos. Mismo criterio y
 * mismo shape mínimo que `apps/capture/src/lib/settings.ts`, para que un
 * dirigente que ya vio la captura reconozca la pantalla.
 */
import { DEFAULT_LOCALE, isLocale, setLocale, type Locale } from '../i18n/index';
import {
  DEFAULT_EFFICIENCY_PRESET,
  isEfficiencyPresetId,
  setEfficiencyPreset,
} from './efficiency';
import { DEFAULT_THEME, applyTheme, isTheme, type Theme } from './theme';

export interface Settings {
  /** Base del backend de la federación, ej. 'https://api.liga.org'. Vacío = mismo origen. */
  apiUrl: string;
  /**
   * Bearer MANUAL: el token único de la federación (`SCOUT_API_TOKEN`), previo
   * a los usuarios. Es el **modo de compatibilidad** para integraciones que no
   * tienen una persona detrás (un tablero embebido, un script).
   *
   * NO es la sesión: la sesión de usuario vive aparte (`src/lib/auth.ts`) y
   * GANA sobre este campo cuando existe. Vacío = sin token manual (que es lo
   * normal desde que hay login).
   */
  apiToken: string;
  /**
   * Base del server de replay VAR **de esta cancha**, ej. 'http://192.168.1.50:8080'.
   * Vacío = sin VAR: la playlist de clips se ve igual (etiquetas y timecodes),
   * pero no hay a quién pedirle el archivo.
   *
   * Es config del NAVEGADOR y no del backend a propósito: el clip sale por la
   * LAN de la cancha, del VAR a quien lo pide, sin pasar por la nube (ADR-017).
   * Por eso vive acá y no en la ficha de la organización.
   */
  replayUrl: string;
  /** Token Bearer del server de replay. Mismo criterio que `replayUrl`. */
  replayToken: string;
  /** Idioma de la interfaz ('es' por defecto). No afecta a lo que publica el API. */
  locale: Locale;
  /**
   * CONVENCIÓN DE EFICIENCIA activa (id de `EFFICIENCY_PRESETS`, ver
   * `lib/efficiency.ts`). Es una preferencia de LECTURA de este navegador —el
   * log guarda evaluaciones, nunca porcentajes—, así que vive acá y no en la
   * ficha de la organización; viaja al API como `?efficiency=<id>`.
   */
  efficiencyPreset: string;
  /**
   * Tema visual (`'var'` por defecto, `'clasico'` = el diseño anterior). Es
   * preferencia de ESTE navegador, como el idioma; ver `lib/theme.ts`.
   */
  theme: Theme;
}

export const SETTINGS_KEY = 'scout.portal.settings';

export const DEFAULT_SETTINGS: Settings = {
  apiUrl: '',
  apiToken: '',
  replayUrl: '',
  replayToken: '',
  locale: DEFAULT_LOCALE,
  efficiencyPreset: DEFAULT_EFFICIENCY_PRESET,
  theme: DEFAULT_THEME,
};

/** Subconjunto de Storage que usamos; permite un fake en tests de Node. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Fallback en memoria: en Node (tests, SSR) no hay localStorage. */
export function createMemoryKeyValueStore(): KeyValueStore {
  const map = new Map<string, string>();
  return {
    getItem: (k) => map.get(k) ?? null,
    setItem: (k, v) => void map.set(k, v),
    removeItem: (k) => void map.delete(k),
  };
}

const fallbackStore = createMemoryKeyValueStore();

export function defaultKeyValueStore(): KeyValueStore {
  const g = globalThis as { localStorage?: KeyValueStore };
  return g.localStorage ?? fallbackStore;
}

export function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

function str(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

/** Normaliza cualquier cosa leída del storage a un Settings completo y válido. */
export function normalizeSettings(raw: unknown): Settings {
  const obj = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  return {
    apiUrl: stripTrailingSlash(str(obj['apiUrl'], DEFAULT_SETTINGS.apiUrl).trim()),
    apiToken: str(obj['apiToken'], DEFAULT_SETTINGS.apiToken).trim(),
    replayUrl: stripTrailingSlash(str(obj['replayUrl'], DEFAULT_SETTINGS.replayUrl).trim()),
    replayToken: str(obj['replayToken'], DEFAULT_SETTINGS.replayToken).trim(),
    locale: isLocale(obj['locale']) ? obj['locale'] : DEFAULT_SETTINGS.locale,
    efficiencyPreset: isEfficiencyPresetId(obj['efficiencyPreset'])
      ? obj['efficiencyPreset']
      : DEFAULT_SETTINGS.efficiencyPreset,
    // Ausente (settings guardados antes del tema) o inválido: el predeterminado.
    theme: isTheme(obj['theme']) ? obj['theme'] : DEFAULT_SETTINGS.theme,
  };
}

export function loadSettings(store: KeyValueStore = defaultKeyValueStore()): Settings {
  try {
    const raw = store.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return normalizeSettings(JSON.parse(raw));
  } catch {
    // Storage corrupto o bloqueado: nunca rompemos el portal por settings.
    return { ...DEFAULT_SETTINGS };
  }
}

/** Guarda normalizando. Acepta un objeto PARCIAL (lo que falte va al default). */
export function saveSettings(
  settings: Partial<Settings>,
  store: KeyValueStore = defaultKeyValueStore(),
): Settings {
  const normalized = normalizeSettings(settings);
  try {
    store.setItem(SETTINGS_KEY, JSON.stringify(normalized));
  } catch {
    // Modo privado / cuota llena: seguimos en memoria.
  }
  return normalized;
}

/**
 * Store observable mínimo para `useSyncExternalStore` (mismo patrón que el
 * runtime de la captura: el estado vive fuera de React).
 */
export class SettingsStore {
  private snapshot: Settings;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly kv: KeyValueStore = defaultKeyValueStore()) {
    this.snapshot = loadSettings(kv);
    // El idioma guardado manda desde el primer render (no hace falta recargar).
    setLocale(this.snapshot.locale);
    // La convención de eficiencia, igual: el store de módulo la publica.
    setEfficiencyPreset(this.snapshot.efficiencyPreset);
    // Y el tema, antes del primer render: sin parpadeo del diseño equivocado.
    applyTheme(this.snapshot.theme);
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => void this.listeners.delete(listener);
  };

  readonly getSnapshot = (): Settings => this.snapshot;

  readonly set = (next: Partial<Settings>): Settings => {
    this.snapshot = saveSettings(next, this.kv);
    // Cambiar el idioma se aplica en vivo: los componentes escuchan el store.
    setLocale(this.snapshot.locale);
    setEfficiencyPreset(this.snapshot.efficiencyPreset);
    applyTheme(this.snapshot.theme);
    for (const listener of this.listeners) listener();
    return this.snapshot;
  };
}
